import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDeleteSlashCommand, useSlashCommands } from '../../../../api/agents-client/agent-client.hooks';
import type { SlashCommand } from '../../../../api/agents-client/agent-client.type';
import { toast } from '../../../../containers/notifications';
import { UserContext } from '../../../../contexts/user';
import { useRoles } from '../../../../helpers/auth-helpers/auth.hooks';
import { useResizableWidth } from '../../../../hooks/useResizableWidth';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Loader } from '../../../../ui-library/loader/loader';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentViewHeader } from '../agent-view-header';
import { AgentCommandPanel } from './agent-command-panel';
import {
  SCOPE_BADGE_BG,
  SCOPE_BADGE_COLORS,
  SCOPE_BADGE_LABEL,
  canEditCommand,
  groupCommandsByScope,
} from '../../../flora/components/chat/slash-commands/agent-commands.helpers';

const MIN_PANEL_WIDTH = 360;
const DEFAULT_PANEL_WIDTH = 560;

export function AgentCommandsView() {
  const { commands, isLoading, error } = useSlashCommands();
  const { isAdmin } = useRoles();
  const { user } = useContext(UserContext);
  const deleteMutation = useDeleteSlashCommand();

  const [panelOpen, setPanelOpen] = useState(false);
  const [editingCommand, setEditingCommand] = useState<SlashCommand | null>(null);
  const [panelReadOnly, setPanelReadOnly] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const {
    width: panelWidth,
    isResizing: isResizingPanel,
    handleMouseDown: startPanelResize,
  } = useResizableWidth({
    initialWidth: DEFAULT_PANEL_WIDTH,
    minWidth: MIN_PANEL_WIDTH,
    maxWidth: () =>
      containerRef.current
        ? Math.max(MIN_PANEL_WIDTH, containerRef.current.getBoundingClientRect().width * 0.7)
        : DEFAULT_PANEL_WIDTH,
    computeWidth: (event) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      return containerRect ? containerRect.right - event.clientX : DEFAULT_PANEL_WIDTH;
    },
  });

  const groups = useMemo(() => groupCommandsByScope(commands), [commands]);

  const handleNewCommand = useCallback(() => {
    setEditingCommand(null);
    setPanelReadOnly(false);
    setPanelOpen(true);
  }, []);

  const handleViewCommand = useCallback((cmd: SlashCommand, editable: boolean) => {
    setEditingCommand(cmd);
    setPanelReadOnly(!editable);
    setPanelOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      if (deletingId) return;
      setDeletingId(id);
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast.success('Command deleted successfully');
        },
        onError: () => {
          toast.error('Failed to delete command');
        },
        onSettled: () => setDeletingId(null),
      });
    },
    [deleteMutation, deletingId],
  );

  const handlePanelClose = useCallback(() => {
    setPanelOpen(false);
    setEditingCommand(null);
    setPanelReadOnly(false);
  }, []);

  if (isLoading) {
    return (
      <Container>
        <MainColumn>
          <AgentViewHeader title="Commands" />
          <LoadingWrapper>
            <Loader />
          </LoadingWrapper>
        </MainColumn>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <MainColumn>
          <AgentViewHeader title="Commands" />
          <EmptyState>
            <Icon name="error" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              Failed to load commands
            </Text>
          </EmptyState>
        </MainColumn>
      </Container>
    );
  }

  return (
    <Container ref={containerRef}>
      <MainColumn>
        <HeaderRow>
          <AgentViewHeader
            title="Commands"
            subtitle={`${commands.length} ${commands.length === 1 ? 'command' : 'commands'}`}
          />
          <Button variant="secondary" size="sm" onClick={handleNewCommand}>
            <Icon name="add" color="#ffffff" size={16} style={{ marginRight: 4, cursor: 'pointer' }} />
            New Command
          </Button>
        </HeaderRow>

        {commands.length === 0 ? (
          <EmptyState>
            <Icon name="terminal" color="var(--ink-lighter)" size={48} />
            <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
              No commands yet
            </Text>
            <Text size="small" color="var(--ink-lighter)">
              Create slash commands to use in Flora conversations.
            </Text>
          </EmptyState>
        ) : (
          <ScrollArea>
            {groups.map((group) => (
              <ScopeSection key={group.scope}>
                <ScopeLabel>
                  <Text size="tiny" weight="bold" color="var(--ink-lighter)">
                    {group.label.toUpperCase()}
                  </Text>
                </ScopeLabel>
                <CommandList>
                  {group.commands.map((cmd) => {
                    const editable = canEditCommand(cmd, isAdmin, user?.id);
                    const isDeleting = deletingId === cmd.id;

                    return (
                      <CommandCard key={cmd.id} onClick={() => handleViewCommand(cmd, editable)} $clickable>
                        <CommandBody>
                          <CommandNameRow>
                            <CommandName size="small" weight="bold" color="var(--ink-darkest)">
                              /{cmd.name}
                            </CommandName>
                            <ScopePill
                              style={{
                                color: SCOPE_BADGE_COLORS[group.scope],
                                background: SCOPE_BADGE_BG[group.scope],
                              }}
                            >
                              <Text size="tiny" weight="medium" color="inherit">
                                {SCOPE_BADGE_LABEL[group.scope]}
                              </Text>
                            </ScopePill>
                          </CommandNameRow>
                          {cmd.description && (
                            <Text size="small" color="var(--ink-base)">
                              {cmd.description}
                            </Text>
                          )}
                        </CommandBody>
                        {editable && (
                          <CardActions onClick={(e) => e.stopPropagation()}>
                            <Menu shadow="sm" radius="md" position="bottom-end" offset={4} withinPortal={false}>
                              <Menu.Target>
                                <MoreButton aria-label="More options" disabled={isDeleting}>
                                  {isDeleting ? (
                                    <Loader size="xs" />
                                  ) : (
                                    <IconifyIcon
                                      icon="material-symbols:more-horiz"
                                      width={16}
                                      height={16}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  )}
                                </MoreButton>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={
                                    <IconifyIcon
                                      icon="material-symbols:edit-outline"
                                      width={14}
                                      height={14}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  }
                                  onClick={() => handleViewCommand(cmd, true)}
                                  style={{ fontSize: '13px' }}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={
                                    <IconifyIcon
                                      icon="material-symbols:delete-outline"
                                      width={14}
                                      height={14}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  }
                                  color="red"
                                  onClick={() => handleDelete(cmd.id)}
                                  style={{ fontSize: '13px' }}
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          </CardActions>
                        )}
                      </CommandCard>
                    );
                  })}
                </CommandList>
              </ScopeSection>
            ))}
          </ScrollArea>
        )}
      </MainColumn>

      <PanelDividerHandle
        onMouseDown={startPanelResize}
        data-dragging={isResizingPanel}
        $isOpen={panelOpen}
        $isResizing={isResizingPanel}
      >
        {panelOpen && (
          <PanelDragDots>
            <IconifyIcon icon="material-symbols:drag-indicator" width={14} height={14} />
          </PanelDragDots>
        )}
      </PanelDividerHandle>
      <PanelColumn
        $isOpen={panelOpen}
        $isResizing={isResizingPanel}
        style={panelOpen ? { width: panelWidth, minWidth: panelWidth } : undefined}
      >
        <AgentCommandPanel
          isOpen={panelOpen}
          onClose={handlePanelClose}
          command={editingCommand}
          isAdmin={isAdmin}
          existingCommands={commands}
          readOnly={panelReadOnly}
        />
      </PanelColumn>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;

const MainColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding: 40px 0;
`;

const PanelDividerHandle = styled.div<{ $isOpen: boolean; $isResizing: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? '5px' : '0px')};
  background: var(--sky-light);
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $isOpen }) => ($isOpen ? 'col-resize' : 'default')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
  transition: ${({ $isResizing }) => ($isResizing ? 'none' : 'width 0.25s ease')};

  &::before {
    content: '';
    position: absolute;
    inset: 0 -3px;
  }

  &:hover,
  &[data-dragging='true'] {
    background: var(--secondary-light);
  }

  &:hover > span,
  &[data-dragging='true'] > span {
    border-color: var(--secondary-light);
    color: var(--secondary-base);
  }
`;

const PanelDragDots = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-lighter);
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 2px 0;
  pointer-events: none;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
`;

const PanelColumn = styled.div<{ $isOpen: boolean; $isResizing: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? `${DEFAULT_PANEL_WIDTH}px` : '0px')};
  min-width: ${({ $isOpen }) => ($isOpen ? `${MIN_PANEL_WIDTH}px` : '0px')};
  overflow: hidden;
  transition: ${({ $isResizing }) => ($isResizing ? 'none' : 'width 0.25s ease, min-width 0.25s ease')};
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-right: 80px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  text-align: center;
  gap: 4px;
  padding: 0 80px;
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0 80px;
`;

const ScopeSection = styled.div`
  margin-bottom: 24px;
`;

const ScopeLabel = styled.div`
  margin-bottom: 12px;
  padding-left: 4px;
`;

const CommandList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CommandCard = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  transition: border-color 0.15s, background 0.15s;
  gap: 12px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    border-color: var(--sky-base);
    background: var(--sky-lightest);
  }
`;

const CommandBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CommandNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommandName = styled(Text)`
  font-family: var(--font-mono, 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace);
  letter-spacing: -0.01em;
`;

const ScopePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 10px;
  line-height: 1;
`;

const CardActions = styled.div`
  flex-shrink: 0;
`;

const MoreButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--ink-lighter);
  transition: background 0.12s, color 0.12s;

  &:hover:not(:disabled) {
    background: var(--sky-lighter);
    color: var(--ink-dark);
  }

  &:disabled {
    cursor: default;
  }
`;
